import classNames from 'classnames';
import { formatCurrencyToPtBr } from '../../../../../utils/currency';
import { useClusterDraftPrice } from '../../hooks/useClusterDraftPrice';
import { ClusterMetrics, NetworkPriceCluster } from '../../types';
import { getClusterLimitBreaks, getClusterMetrics, isClusterAvailable } from '../../utils';
import { NetworkVariationIndicator } from '../NetworkVariationIndicator';
import styles from './ClusterDetailCells.module.scss';

type ClusterDetailCellProps = { cluster: NetworkPriceCluster };
type PercentageMetric = 'cpi' | 'margin';

const percentageFormatter = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const PERCENTAGE_METRICS: Record<PercentageMetric, { value: keyof ClusterMetrics; variation: keyof ClusterMetrics; highlightsNegative: boolean }> = {
  cpi: { value: 'cpi', variation: 'cpiVariation', highlightsNegative: false },
  margin: { value: 'margin', variation: 'marginVariation', highlightsNegative: true },
};

export const UnavailableClusterDetailCell = () => <span className={styles.value}>--</span>;

const ClusterPercentageValue = ({ cluster, metric }: ClusterDetailCellProps & { metric: PercentageMetric }) => {
  const draft = useClusterDraftPrice(cluster.productsToBePricedId);
  if (!isClusterAvailable(cluster)) return <UnavailableClusterDetailCell />;
  const config = PERCENTAGE_METRICS[metric];
  const metrics = getClusterMetrics(cluster, draft ?? undefined);
  const value = metrics[config.value];
  // No input to outline here: a broken limit paints the value with the input's broken-limit red.
  const isBroken = getClusterLimitBreaks(cluster, draft)[metric];

  return (
    <div className={styles.metric}>
      <span
        className={classNames(
          styles.metricValue,
          config.highlightsNegative && value !== null && value < 0 && styles.negative,
          isBroken && styles.brokenLimit,
        )}
      >
        {value === null ? '--' : `${percentageFormatter.format(value)}%`}
      </span>
      <NetworkVariationIndicator value={metrics[config.variation]} suffix=" p.p." />
    </div>
  );
};

export const ClusterCpiCell = ({ cluster }: ClusterDetailCellProps) => <ClusterPercentageValue cluster={cluster} metric="cpi" />;

export const ClusterMarginCell = ({ cluster }: ClusterDetailCellProps) => <ClusterPercentageValue cluster={cluster} metric="margin" />;

const ClusterCurrencyValue = ({ cluster, value }: ClusterDetailCellProps & { value: number | null }) => {
  if (!isClusterAvailable(cluster) || value === null) return <UnavailableClusterDetailCell />;

  return <span className={styles.value}>{formatCurrencyToPtBr(value)}</span>;
};

export const ClusterPmzCell = ({ cluster }: ClusterDetailCellProps) => <ClusterCurrencyValue cluster={cluster} value={cluster.pmz} />;

export const ClusterRetailPriceCell = ({ cluster }: ClusterDetailCellProps) => <ClusterCurrencyValue cluster={cluster} value={cluster.retailPrice} />;

export const ClusterCompetitorPriceCell = ({ cluster }: ClusterDetailCellProps) => <ClusterCurrencyValue cluster={cluster} value={cluster.competitorsPrice} />;
